/**
 * 分享报告只读页（四期 K）：/reports/share?token=xxx
 * - 无需登录，token 鉴权；只展示统计与步骤明细，无任何操作入口
 * - 链接失效/关闭分享时给出提示
 */
import { useEffect, useState } from 'react'
import { Button, Result, Space, Spin, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckCircleOutlined, CloseCircleOutlined, ShareAltOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import { getSharedReport, type TestReport, type TestReportStep } from '@/api/reports'
import { useThemeStore } from '@/stores/theme'
import { StepResultDetail, UIStepResultDetail } from './StepDetail'
import dayjs from 'dayjs'
import '../../styles/aurora-page.css'

const { Title, Text } = Typography

const taskTypeText: Record<number, string> = { 1: '接口场景测试', 2: 'UI测试' }
const stepTypeText: Record<number, string> = {
  1: 'HTTP请求', 2: '等待控制', 3: '条件控制', 4: '循环控制', 5: '断言控制', 6: '数据库', 7: '脚本',
  8: 'UI操作', 9: 'AI步骤', 10: 'UI断言',
}
const stepStatusText: Record<number, string> = { 1: '成功', 2: '失败', 3: '错误', 4: '跳过' }
const stepStatusColor: Record<number, string> = { 1: 'success', 2: 'error', 3: 'warning', 4: 'default' }

const formatDuration = (ms: number) => {
  if (!ms || ms <= 0) return '-'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m${Math.round((ms % 60000) / 1000)}s`
}

function StatRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, fontSize: 14 }}>
      <span style={{ color: 'var(--au-slate-muted)', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontWeight: 800, color: 'var(--au-slate)', textAlign: 'right', wordBreak: 'break-all' }}>{children}</span>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  padding: 18,
  borderRadius: 12,
  background: 'var(--au-surface)',
  border: '1px solid var(--au-border)',
  boxShadow: 'var(--au-shadow-soft)',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--au-slate-muted)',
  letterSpacing: '0.04em',
  marginBottom: 14,
}

export default function SharedReport() {
  const { isDark } = useThemeStore()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const [report, setReport] = useState<TestReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) {
      setError('分享链接无效（缺少 token）')
      setLoading(false)
      return
    }
    ;(async () => {
      try {
        const detail = await getSharedReport(token)
        setReport(detail)
      } catch {
        setError('分享链接已失效或报告分享已关闭')
      } finally {
        setLoading(false)
      }
    })()
  }, [token])

  const columns: ColumnsType<TestReportStep> = [
    { title: '#', width: 52, align: 'center', render: (_: unknown, __: TestReportStep, i: number) => i + 1 },
    {
      title: '步骤名称',
      dataIndex: 'step_name',
      render: (v: string, record: TestReportStep) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            paddingLeft: record.depth > 0 ? 20 : 0,
            fontWeight: record.depth > 0 ? 400 : 600,
          }}
        >
          {record.depth > 0 ? (
            '└ '
          ) : (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flexShrink: 0,
                background:
                  record.status === 2 || record.status === 3
                    ? 'var(--au-terracotta)'
                    : record.status === 4
                      ? 'var(--au-slate-muted)'
                      : 'var(--au-primary)',
              }}
            />
          )}
          {v || '-'}
        </span>
      ),
    },
    { title: '步骤类型', dataIndex: 'step_type', width: 110, render: (v: number) => <Tag>{stepTypeText[v] || '未知'}</Tag> },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (v: number) => <Tag color={stepStatusColor[v] || 'default'}>{stepStatusText[v] || '未知'}</Tag>,
    },
    { title: '耗时', dataIndex: 'duration_ms', width: 100, render: formatDuration },
  ]

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-header-bg, #f6f7fb)' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error || !report) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--au-header-bg, #f6f7fb)' }}>
        <Result
          status="404"
          title="无法访问分享报告"
          subTitle={error}
          extra={<Button type="primary" href="/" style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}>前往 AuroraTest</Button>}
        />
      </div>
    )
  }

  const hasFailure = (report.failed_steps ?? 0) > 0 || (report.error_steps ?? 0) > 0
  return (
    <div className="aurora-page" style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 24px 40px', minHeight: '100vh', gap: 16 }}>
      {/* 头部：只读标识 + 报告名 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Space>
          <ShareAltOutlined style={{ color: 'var(--au-primary)', fontSize: 20 }} />
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>{report.name || `报告 #${report.id}`}</Title>
        </Space>
        <Space>
          <Tag color="blue">{taskTypeText[report.task_type] || '未知'}</Tag>
          <Tag color="default">只读分享</Tag>
        </Space>
      </div>

      {/* 统计区 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>结果概览</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <StatRow label="执行结果">
              <Space size={6}>
                {report.run_result === 1 ? (
                  <><CheckCircleOutlined style={{ color: 'var(--au-primary)' }} /><span style={{ color: 'var(--au-primary)' }}>成功</span></>
                ) : (
                  <><CloseCircleOutlined style={{ color: 'var(--au-terracotta)' }} /><span style={{ color: 'var(--au-terracotta)' }}>失败</span></>
                )}
              </Space>
            </StatRow>
            <StatRow label="通过率">
              <span style={{ color: (Number(report.pass_rate) || 0) >= 100 ? 'var(--au-primary)' : '#c98d2b' }}>
                {Number(report.pass_rate) || 0}%
              </span>
            </StatRow>
          </div>
        </div>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>步骤统计</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <StatRow label="总步骤">{report.total_steps ?? 0}</StatRow>
            <StatRow label="成功 / 失败 / 错误 / 跳过">
              <span style={{ color: 'var(--au-primary)' }}>{report.success_steps ?? 0}</span>
              <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
              <span style={{ color: 'var(--au-terracotta)' }}>{report.failed_steps ?? 0}</span>
              <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
              <span style={{ color: '#c98d2b' }}>{report.error_steps ?? 0}</span>
              <span style={{ color: 'var(--au-slate-muted)' }}> / </span>
              <span style={{ color: 'var(--au-slate-muted)' }}>{report.skipped_steps ?? 0}</span>
            </StatRow>
          </div>
        </div>
        <div style={cardStyle}>
          <div style={cardTitleStyle}>执行信息</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <StatRow label="执行时间">
              {(report.run_time || report.create_time) ? dayjs(report.run_time || report.create_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </StatRow>
            <StatRow label="总耗时">{formatDuration(report.duration_ms)}</StatRow>
          </div>
        </div>
      </div>

      {/* 运行摘要 */}
      {report.summary && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            background: hasFailure ? 'var(--au-terracotta-light)' : 'var(--au-primary-50)',
            border: hasFailure ? '1px solid var(--au-terracotta)' : '1px solid var(--au-primary-100)',
            borderRadius: 12,
            color: hasFailure ? 'var(--au-terracotta)' : 'var(--au-primary-dark)',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {hasFailure ? (
            <CloseCircleOutlined style={{ fontSize: 18, color: 'var(--au-terracotta)', flexShrink: 0 }} />
          ) : (
            <CheckCircleOutlined style={{ fontSize: 18, color: 'var(--au-primary)', flexShrink: 0 }} />
          )}
          <span>{report.summary}</span>
        </div>
      )}

      {/* AI 摘要（四期 L：生成过则随分享展示） */}
      {report.ai_summary && (
        <div style={{ ...cardStyle, padding: 16 }}>
          <div style={cardTitleStyle}>AI 摘要</div>
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 10,
              background: 'var(--au-primary-50)',
              border: '1px solid var(--au-primary-100)',
              fontSize: 13,
              lineHeight: '22px',
              color: 'var(--au-slate)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {report.ai_summary}
          </div>
        </div>
      )}

      {/* 步骤执行明细（可展开查看每个步骤的执行详情；仅展示结果数据，无内部操作入口） */}
      <div style={{ ...cardStyle, padding: '0 0 8px' }}>
        <div style={{ ...cardTitleStyle, padding: '14px 18px 0' }}>步骤执行明细（共 {report.steps?.length ?? 0} 条）</div>
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={report.steps || []}
          pagination={false}
          locale={{ emptyText: '无步骤明细' }}
          rowClassName={(record: TestReportStep) => (record.depth > 0 ? 'report-step-child-row' : '')}
          expandable={{
            expandedRowRender: (record: TestReportStep) =>
              report.task_type === 2 ? (
                <UIStepResultDetail record={record} isDark={isDark} />
              ) : (
                <StepResultDetail record={record} isDark={isDark} />
              ),
            rowExpandable: () => true,
          }}
        />
      </div>

      <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
        该页面为只读分享视图，仅展示报告统计与步骤结果；如需操作请登录 AuroraTest 平台。
      </Text>
    </div>
  )
}
